import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const PAID_FROM_OPTIONS = ["EW IDFC Bank", "Other"];

const EMPTY = {
  expense_date: "", title: "", category: "", amount: "", gst_amount: "", payment_method: "",
  paid_from: "EW IDFC Bank", paid_from_other: "", vendor: "", reference_no: "", department: "", notes: "",
};

export default function ExpenseFormDialog({ open, onOpenChange, options, expense, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!expense;

  useEffect(() => {
    if (open) {
      if (expense) {
        const knownAccount = PAID_FROM_OPTIONS.includes(expense.paid_from);
        setForm({
          expense_date: expense.expense_date, title: expense.title, category: expense.category,
          amount: String(expense.amount), gst_amount: expense.gst_amount != null ? String(expense.gst_amount) : "",
          payment_method: expense.payment_method, paid_from: knownAccount ? expense.paid_from : "Other",
          paid_from_other: knownAccount ? "" : expense.paid_from, vendor: expense.vendor || "",
          reference_no: expense.reference_no || "", department: expense.department, notes: expense.notes || "",
        });
      } else {
        setForm(EMPTY);
      }
      setFile(null);
    }
  }, [open, expense]);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));
  const setField = (k) => (v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    const paidFrom = form.paid_from === "Other" ? form.paid_from_other.trim() : form.paid_from;
    if (!form.expense_date || !form.title.trim() || !form.category || !form.amount || !form.payment_method || !paidFrom || !form.department) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`/expenses/${expense.id}`, {
          expense_date: form.expense_date, title: form.title.trim(), category: form.category,
          amount: parseFloat(form.amount), gst_amount: form.gst_amount ? parseFloat(form.gst_amount) : null,
          payment_method: form.payment_method, paid_from: paidFrom, vendor: form.vendor.trim() || null,
          reference_no: form.reference_no.trim() || null, department: form.department, notes: form.notes.trim() || null,
        });
        if (file) {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(`/expenses/${expense.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        toast.success("Expense updated");
      } else {
        const fd = new FormData();
        fd.append("expense_date", form.expense_date);
        fd.append("title", form.title.trim());
        fd.append("category", form.category);
        fd.append("amount", form.amount);
        if (form.gst_amount) fd.append("gst_amount", form.gst_amount);
        fd.append("payment_method", form.payment_method);
        fd.append("paid_from", paidFrom);
        fd.append("vendor", form.vendor.trim());
        fd.append("reference_no", form.reference_no.trim());
        fd.append("department", form.department);
        fd.append("notes", form.notes.trim());
        if (file) fd.append("file", file);
        await api.post("/expenses", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Expense recorded");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="expense-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Expense" : "Record New Expense"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Expense Date *</Label>
              <Input data-testid="expense-date-input" type="date" value={form.expense_date} onChange={set("expense_date")} />
            </div>
            <div className="space-y-1.5">
              <Label>Amount (₹) *</Label>
              <Input data-testid="expense-amount-input" type="number" min="0" step="0.01" value={form.amount} onChange={set("amount")} placeholder="0.00" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Expense Title *</Label>
            <Input data-testid="expense-title-input" value={form.title} onChange={set("title")} placeholder="e.g. Office Rent July" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={setField("category")}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{options.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Department *</Label>
              <Select value={form.department} onValueChange={setField("department")}>
                <SelectTrigger data-testid="expense-department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>{options.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Payment Method *</Label>
              <Select value={form.payment_method} onValueChange={setField("payment_method")}>
                <SelectTrigger data-testid="expense-payment-method-select"><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>{options.payment_methods.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>GST Amount (Optional)</Label>
              <Input data-testid="expense-gst-input" type="number" min="0" step="0.01" value={form.gst_amount} onChange={set("gst_amount")} placeholder="0.00" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Paid From *</Label>
              <Select value={form.paid_from} onValueChange={setField("paid_from")}>
                <SelectTrigger data-testid="expense-paid-from-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAID_FROM_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.paid_from === "Other" && (
              <div className="space-y-1.5">
                <Label>Specify Account</Label>
                <Input data-testid="expense-paid-from-other-input" value={form.paid_from_other} onChange={set("paid_from_other")} placeholder="e.g. Petty Cash" />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Vendor/Payee</Label>
              <Input data-testid="expense-vendor-input" value={form.vendor} onChange={set("vendor")} placeholder="e.g. Skyline Properties" />
            </div>
            <div className="space-y-1.5">
              <Label>Reference/Invoice No.</Label>
              <Input data-testid="expense-reference-input" value={form.reference_no} onChange={set("reference_no")} placeholder="e.g. INV-2201" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea data-testid="expense-notes-input" value={form.notes} onChange={set("notes")} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label>{isEdit && expense?.attachment_filename ? `Attachment (current: ${expense.attachment_filename})` : "Attachment (Invoice/Bill)"}</Label>
            <Input data-testid="expense-attachment-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="expense-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="expense-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Record Expense"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
